/**
 * Data classes for everything that may enter an LLM prompt — ADR-0073 §2 / V105-2-2.
 *
 * Three classes, lowest to highest:
 *   reference — public or GAC Pro-authored content holding nothing about a customer
 *   tenant    — the customer's own data and facts derived from it
 *   federal   — federal customer data (FRD-FCD) or content likely to hold it
 *
 * Every value that reaches a prompt is a `Classified` value made by `field()`
 * (or `derive()` / `referenceDerived()`), keyed by an entry in `PROMPT_FIELDS`.
 * Prompt instructions are `PromptTemplate`s whose text is static, reviewed code
 * (`defineTemplate`); data only ever enters through named slots. The LLM gateway
 * (gateway.ts) accepts nothing else, so a prompt's class profile is always
 * known. The CI guard (`scripts/check-llm-egress.mjs`, rule G2) rejects a
 * template built from data and an unregistered field key.
 *
 * Owner decisions (2026-10-08): evidence artifact bodies and incident
 * descriptions default to `tenant`; NO code default is `federal`. A workspace
 * raises a field (or every tenant field, key '*') to `federal` — never lowers.
 *
 * Free text never declassifies and redaction is not a path (D-105-11): an LLM
 * output carries the highest class of its prompt; only a REFERENCE_DERIVATIONS
 * function whose output is drawn from a closed reference vocabulary yields a
 * `reference` value from higher-class input.
 */
import { FRAMEWORK_IDS } from '../data/framework-ids.js';
export const DATA_CLASSES = ['reference', 'tenant', 'federal'];
export const CLASS_RANK = { reference: 0, tenant: 1, federal: 2 };
export function maxClass(...classes) {
    let best = 'reference';
    for (const c of classes)
        if (CLASS_RANK[c] > CLASS_RANK[best])
            best = c;
    return best;
}
export function isDataClass(v) {
    return v === 'reference' || v === 'tenant' || v === 'federal';
}
/**
 * Every value that may enter a prompt has an entry here. Adding a key is a
 * reviewed code change (the owner is the feature that supplies the value).
 * One row per field used by the call sites in ADR-0073 Context §1.
 */
export const PROMPT_FIELDS = {
    // ---- reference: public / GAC Pro-authored ----------------------------------
    'register.risk_item_text': { class: 'reference', owner: 'register', label: 'Register item text (public register)' },
    'register.control_text': { class: 'reference', owner: 'register', label: 'Control text (global catalog)' },
    'research.source_text': { class: 'reference', owner: 'research', label: 'Research-source page text (public)' },
    // V111-7-5: a finding excerpt with candidate register rows (both reference) for the variant re-rank.
    'research.match_payload': { class: 'reference', owner: 'research', label: 'Finding with candidate register rows (variant re-rank)' },
    // V112-4-2: one legal duty (public text) with candidate register controls (catalog text) for the duty re-rank.
    'research.duty_match_payload': { class: 'reference', owner: 'research', label: 'Legal duty with candidate register controls (duty re-rank)' },
    'research.legislation_text': { class: 'reference', owner: 'rules', label: 'Legislation text and metadata (public)' },
    'rules.rule_text': { class: 'reference', owner: 'rules', label: 'Jurisdiction rule text (global catalog)' },
    'rules.vocabulary': { class: 'reference', owner: 'rules', label: 'Closed vocabularies (rule domains, risk vectors, categories)' },
    'remediation.hint_steps': { class: 'reference', owner: 'remediation', label: 'Reviewed remediation hint steps' },
    'check.reference': { class: 'reference', owner: 'remediation', label: 'Check key, title and control id (code identifiers)' },
    'eval.golden_case': { class: 'reference', owner: 'evals', label: 'Synthetic golden-case input (reference-only, ADR-0073 §6)' },
    'derived.reference': { class: 'reference', owner: 'egress', label: 'Closed-vocabulary derivation (counts, ids, enums)' },
    // ADR-0075 §7: the authoring-time research agent's only inputs (provider / product refs from the
    // registry, a strategy family and a question template id: code vocabularies, never customer data).
    'substitution.research_inputs': { class: 'reference', owner: 'substitution-catalog', label: 'Catalog research inputs (registry refs, strategy family, question id)' },
    // ---- tenant: the customer's own data ----------------------------------------
    'questionnaire.document_text': { class: 'tenant', owner: 'questionnaires', label: 'Uploaded questionnaire text' },
    'questionnaire.sheet_sample': { class: 'tenant', owner: 'questionnaires', label: 'Questionnaire spreadsheet headers and sample rows' },
    'questionnaire.question_text': { class: 'tenant', owner: 'questionnaires', label: 'Questionnaire questions' },
    'questionnaire.match_payload': { class: 'tenant', owner: 'questionnaires', label: 'Questions with candidate register items (re-rank)' },
    'questionnaire.draft_payload': { class: 'tenant', owner: 'questionnaires', label: 'Questions, citations and evidence excerpts (answer drafting)' },
    'policy_doc.text': { class: 'tenant', owner: 'control-specs', label: 'Uploaded policy document text' },
    'policy_draft.grounding': { class: 'tenant', owner: 'policy-drafts', label: 'Profile, team, readiness and register grounding for policy drafts' },
    'customer_profile.facts': { class: 'tenant', owner: 'profile', label: 'Customer profile facts' },
    'finding.check_snapshot': { class: 'tenant', owner: 'findings', label: 'Failing check snapshot (customer system data)' },
    'finding.evidence_ref': { class: 'tenant', owner: 'findings', label: 'Evidence hash and observation time' },
    'evidence.artifact_text': { class: 'tenant', owner: 'evidence', label: 'Evidence artifact bodies and excerpts' }, // owner 2026-10-08: raise per workspace
    'incident.description': { class: 'tenant', owner: 'incidents', label: 'Incident descriptions' }, // owner 2026-10-08: raise per workspace
    'ai_system.description': { class: 'tenant', owner: 'ai-systems', label: 'AI system name, description and known jurisdictions' },
    'ai_system.documents': { class: 'tenant', owner: 'ai-systems', label: 'Supporting documents for an AI system' },
    'ai_system.card': { class: 'tenant', owner: 'disclosures', label: 'AI system card' },
    'disclosure.content': { class: 'tenant', owner: 'disclosures', label: 'Disclosure content (draft, prior or released)' },
    'disclosure.buyer_questions': { class: 'tenant', owner: 'disclosures', label: 'Buyer questions about a disclosure' },
    'release.gate_events': { class: 'tenant', owner: 'releases', label: 'Gate denials and drift events' },
    'blueprint.inventory': { class: 'tenant', owner: 'blueprints', label: 'Blueprint inventory, gaps and options' },
    'obligation.system_obligations': { class: 'tenant', owner: 'obligations', label: 'A system’s obligations and their status' },
    'infra.verdict_inputs': { class: 'tenant', owner: 'infra-plan', label: 'Inventory items and verdict answers (ADR-0072 Q1/Q2)' },
    // Values computed by an LLM from a prompt: class = the prompt's max class (taint).
    'llm.output': { class: 'tenant', owner: 'egress', label: 'LLM output (carries its prompt’s class)' },
};
export const FIELD_KEYS = Object.keys(PROMPT_FIELDS);
export function isFieldKey(k) {
    return typeof k === 'string' && Object.prototype.hasOwnProperty.call(PROMPT_FIELDS, k);
}
export function defaultClassOf(key) {
    return PROMPT_FIELDS[key].class;
}
const MINTED = new WeakSet();
/** Runtime check that `v` was minted here (an `as` cast does not pass). */
export function isClassified(v) {
    return typeof v === 'object' && v !== null && MINTED.has(v);
}
function mint(key, value, derivedFrom, floor) {
    if (!isFieldKey(key))
        throw new Error(`egress: unregistered prompt field '${String(key)}' (add it to PROMPT_FIELDS)`);
    if (typeof value !== 'string')
        throw new Error(`egress: field '${key}' value must be a string`);
    for (const d of derivedFrom ?? [])
        if (!isFieldKey(d))
            throw new Error(`egress: unregistered derivedFrom field '${String(d)}'`);
    const o = Object.freeze({ field: key, value, ...(derivedFrom?.length ? { derivedFrom: Object.freeze([...derivedFrom]) } : {}), ...(floor ? { floor } : {}) });
    MINTED.add(o);
    return o;
}
/** Classify a value under a registered field key. */
export function field(key, value) {
    return mint(key, value);
}
/**
 * A value computed from other classified inputs. Its class is the maximum of
 * its own key's class and every input's effective class (taint). Pass an LLM
 * result's `outputClass` (or a `Classified`) to carry a floor.
 */
export function derive(key, value, from) {
    const keys = [];
    let floor = 'reference';
    for (const f of from) {
        if (isClassified(f)) {
            keys.push(f.field, ...(f.derivedFrom ?? []));
            if (f.floor)
                floor = maxClass(floor, f.floor);
        }
        else if (isDataClass(f)) {
            floor = maxClass(floor, f);
        }
        else if (isFieldKey(f)) {
            keys.push(f);
        }
        else {
            throw new Error(`egress: derive() input is neither a classified value, a field key nor a class`);
        }
    }
    return mint(key, value, [...new Set(keys)], floor === 'reference' ? undefined : floor);
}
/**
 * Storage lineage (V105-2-10 H-1). Some fields hold text that was STORED from
 * another field's content, so the taint of `derive()` is lost at the database:
 * the questions of a questionnaire are lines of the uploaded document or
 * spreadsheet. A raise on the source field must follow the text, or raising
 * `questionnaire.document_text` would protect only the extract call while the
 * same text leaves as `questionnaire.question_text` in matching and drafting.
 * A field's effective class is at least each source field's effective class.
 * A raise only ever flows downstream (source → stored copy), never upstream.
 *
 * V105-2-10 M-4 (the general case): the ADR-0039 agents that STORE derived text.
 *   - A1 intake elicitation reads `ai_system.documents` (and the description) and
 *     its approved proposals are stored as the system's name / purpose, which every
 *     later prompt sends as `ai_system.description`.
 *   - A3 disclosure drafting reads the description, the system card and evidence
 *     excerpts; its drafts are stored as `disclosure.content`, which A3 (prior
 *     version), A3b and A8 send again.
 * So a raise on any of those sources follows the stored text.
 */
export const FIELD_LINEAGE = {
    'questionnaire.question_text': ['questionnaire.document_text', 'questionnaire.sheet_sample'],
    'questionnaire.match_payload': ['questionnaire.question_text'],
    'questionnaire.draft_payload': ['questionnaire.question_text'],
    // A1 (agent-intake-elicitation-service): documents → approved name / purpose.
    'ai_system.description': ['ai_system.documents'],
    // A3 (agent-disclosure-drafting-service): description + card + evidence → stored draft.
    'disclosure.content': ['ai_system.description', 'ai_system.card', 'evidence.artifact_text'],
};
export function effectiveFieldClass(key, overrides) {
    return effectiveFieldClassVisit(key, overrides, new Set());
}
function effectiveFieldClassVisit(key, overrides, seen) {
    const base = defaultClassOf(key);
    if (!overrides || overrides.size === 0)
        return base;
    seen.add(key);
    let c = base;
    const own = overrides.get(key);
    if (own)
        c = maxClass(c, own);
    // '*' raises every tenant-default field; reference content is never "about" the customer.
    const all = overrides.get('*');
    if (all && base === 'tenant')
        c = maxClass(c, all);
    for (const src of FIELD_LINEAGE[key] ?? []) {
        if (!seen.has(src))
            c = maxClass(c, effectiveFieldClassVisit(src, overrides, seen));
    }
    return c;
}
/** Effective class of a classified value: max(own key, every derivedFrom key, floor). */
export function effectiveClass(v, overrides) {
    let c = effectiveFieldClass(v.field, overrides);
    for (const k of v.derivedFrom ?? [])
        c = maxClass(c, effectiveFieldClass(k, overrides));
    if (v.floor)
        c = maxClass(c, v.floor);
    return c;
}
// ---- Reviewed reference derivations ------------------------------------------------
const FRAMEWORK_ID_SET = new Set(Object.values(FRAMEWORK_IDS));
/**
 * Pure functions whose output is drawn from a closed reference vocabulary.
 * Only these declassify. Each ships a test proving every output is in its
 * vocabulary (`egress/data-classes.test.ts`). An LLM output is never one.
 */
export const REFERENCE_DERIVATIONS = {
    /** A non-negative integer count, as decimal digits. */
    count: (n) => String(Number.isFinite(n) && n > 0 ? Math.trunc(n) : 0),
    /** Canonical framework ids only (anything else is dropped), comma-separated. */
    framework_ids: (ids) => [...new Set(ids.filter((id) => FRAMEWORK_ID_SET.has(id)))].sort().join(', '),
    /** A value from a caller-supplied closed enum (code constant); anything else becomes ''. */
    enum_value: (value, vocabulary) => (vocabulary.includes(value) ? value : ''),
    /** An ISO date (YYYY-MM-DD) or ''. */
    iso_date: (value) => (/^\d{4}-\d{2}-\d{2}$/.test(value.slice(0, 10)) ? value.slice(0, 10) : ''),
};
/** Run a reviewed reference derivation; the result is `reference` class whatever its input. */
export function referenceDerived(name, ...args) {
    const fn = REFERENCE_DERIVATIONS[name];
    return mint('derived.reference', fn(...args));
}
const TEMPLATES = new WeakSet();
const TEMPLATE_IDS = new Map();
const SLOT_NAME = /^[a-z][a-z0-9_]{0,40}$/;
/**
 * Prompt instructions are static, reviewed text with named slots; never
 * concatenated with data. The CI guard (G2) requires `text` to be a literal
 * (or a const built only from literals). `{slot}` placeholders must be
 * declared; a declared slot that the text never uses is an error.
 */
export function defineTemplate(id, version, text, slots) {
    if (!/^[a-z0-9][a-z0-9._-]{1,80}$/i.test(id))
        throw new Error(`egress: bad template id '${id}'`);
    if (!version)
        throw new Error(`egress: template '${id}' needs a version`);
    for (const s of slots) {
        if (!SLOT_NAME.test(s))
            throw new Error(`egress: template '${id}' slot '${s}' is not a valid name`);
        if (!text.includes(`{${s}}`))
            throw new Error(`egress: template '${id}' declares slot '${s}' but never uses it`);
    }
    // id@version names one text (the ledger records it as prompt_version): the same pair can't mean two prompts.
    const key = `${id}@${version}`;
    const prior = TEMPLATE_IDS.get(key);
    if (prior !== undefined && prior !== text)
        throw new Error(`egress: template '${key}' defined twice with different text`);
    TEMPLATE_IDS.set(key, text);
    const t = Object.freeze({ id, version, text, slots: Object.freeze([...slots]) });
    TEMPLATES.add(t);
    return t;
}
export function isTemplate(v) {
    return typeof v === 'object' && v !== null && TEMPLATES.has(v);
}
/**
 * Fill a template's slots in ONE pass: a slot value that itself contains
 * `{other}` is never expanded, so data cannot inject a placeholder.
 */
export function renderTemplate(t, slots) {
    const names = new Set(t.slots);
    for (const k of Object.keys(slots))
        if (!names.has(k))
            throw new Error(`egress: template '${t.id}' has no slot '${k}'`);
    for (const s of t.slots)
        if (!(s in slots))
            throw new Error(`egress: template '${t.id}' slot '${s}' was not filled`);
    if (t.slots.length === 0)
        return t.text;
    const re = new RegExp(`\\{(${t.slots.map((s) => s.replace(/[^a-z0-9_]/g, '')).join('|')})\\}`, 'g');
    return t.text.replace(re, (_m, name) => slots[name].value);
}
//# sourceMappingURL=data-classes.js.map